// Release 1.5.0 — which pages read which data areas (D-O93 / O-R54). When the server says an area moved, only the queries
// whose key root is mapped to that area are refetched; the rest of the tab is left alone.
//
// Areas are the server's WatermarkKinds (Domain\Rules\Insights\InsightModels.cs) minus the plant day.
// A query root missing from QUERY_ROOT_AREAS fails safe: it depends on EVERY area (refetched on any change, which is what
// the 30-second check did for every query before 1.5.0). A root mapped to [] reads only bookkeeping (who is signed in,
// notifications, releases, AI logs …) that no data area covers; those keep their own refresh intervals.

export const LIVE_AREAS = [
  "gss.batch_id",
  "plan.ledger_seq",
  "gov.settings_version",
  "people.version",
  "cust.version",
  "ref.version",
  "floor.version",
  "supply.version",
  "quality.version",
  "kb.version",
  "app.other"
] as const;

export type LiveArea = (typeof LIVE_AREAS)[number];

const G = "gss.batch_id";
const P = "plan.ledger_seq";
const S = "gov.settings_version";
const PE = "people.version";
const C = "cust.version";
const R = "ref.version";
const F = "floor.version";
const SU = "supply.version";
const Q = "quality.version";
const K = "kb.version";

/** Areas that may change any computed figure: a governed setting, or a table nobody has classified yet. */
export const EVERYTHING_AREAS: readonly LiveArea[] = [S, "app.other"];

/** Depends on every area. */
export const ALL = "all" as const;

/**
 * Query-key root → the data areas its pages read. The root is the key's first element; for the roots in
 * TWO_LEVEL_ROOTS ("admin", "header") it is "<root>/<second element>". Every root the SPA uses is listed (a vitest guard checks the api files).
 */
export const QUERY_ROOT_AREAS: Readonly<Record<string, readonly LiveArea[] | typeof ALL>> = {
  // Plant figures built from GSS plus the planning layer and the floor records kept here.
  operations: ALL,
  shopfloor: [G, P, R, F, SU, PE],
  floor: [G, P, R, F, SU, PE],
  flow: [G, P, R, F, SU],
  schedule: [G, P, R, F],
  planning: [G, P, R, F],
  rebalance: [G, P, R],
  // Release 1.6.0 VIPER Rebalance: staleness and the candidates' figures follow the plan (its own tables are bookkeeping).
  "viper-rebalance": [G, P, R],
  kanban: [G, P, R, F, PE],
  supply: [G, P, R, SU],
  "purchasing-workspace": [G, SU],
  // Release 1.7.0 (D-O141): packing lists (supply records) and the GSS vendors and PO shipping they prefill from.
  "packing-lists": [G, SU],
  // Release 1.9.0 W2: the OSP lifecycle line and Logistics inbox (GSS receipts, supply records and the logi tables).
  "logistics-handoff": [G, SU],
  "purchasing-rfq": [G, SU],
  "vendor-scorecard": [G, SU],
  // Release 1.20.0 (O-R226): the Morning Huddle composes the shipping, launch, OSP and capacity boards plus its own
  // planning-layer commitments.
  huddle: [G, P, R, F, SU],
  // Release 1.12.0: the Open PO Radar reads the GSS PO lines and the imported spreadsheet rows.
  "purchasing-open-pos": [G, SU],
  // Release 1.9.0: shipments, tracking and carriers (logi.* tables are the supply area, DataChangeAreas).
  logistics: [G, SU],
  customers: [G, P, C],
  "customer-brief-schedules": [C],
  "customer-health-history": [G, C],
  present: [G, P, C],
  people: [G, P, PE],
  // Who runs which machine (2026-10-23): GSS punches, the roster and planned time off.
  "machine-patterns": [G, P, PE],
  training: [PE],
  profile: [PE],
  knowledge: [K],
  quality: [Q],
  as9100: [Q],
  cmmc: [Q],
  // Cross-cutting views: SQDC, wall board, program LOB, search, KPIs, charts, VIPER insights, the header.
  parity: ALL,
  analytics: ALL,
  charts: ALL,
  "chart-drill": ALL,
  insights: ALL,
  // Release 1.5.0 (viper-inline): work-order glances, Why? panels and chart explanations read the computed plant figures.
  "viper-inline": ALL,
  header: ALL,
  "header/search": ALL,
  "header/freshness": [G],
  "header/photo": [],
  "header/step-up": [],
  // Notices are bookkeeping, but usually caused by a data change or a job (notify-1-5): refresh on any move.
  "notification-centre": ALL,
  // Release 1.5.0 Home (role-home-1-5b): the person's own start page, tiles and saved filters — bookkeeping only.
  home: [],
  "admin/start-pages": [],
  custody: ALL,
  "admin/audit": ALL,
  "admin/data-sync": [G],
  "admin/data-health": [G],
  "admin/governed": [S],
  // Release 1.9.0: carrier admin (logi.carriers / carrier_accounts — supply area).
  "admin/logistics": [SU],
  "admin/purchasing": [SU],
  "admin/knowledge-index": [K],
  // Bookkeeping only: never moved by a data area.
  me: [],
  notifications: [],
  releases: [],
  viper: [],
  ai: [],
  "pilot-feedback": [],
  access: [],
  // O-R56: Entra names, photos and Admin edits (bookkeeping tables, never a plant figure).
  directory: [],
  "access-requests": [],
  "access-review": [],
  "stakeholder-questions": [],
  reports: [],
  "teams-approvals": [],
  "admin/adoption": [],
  "admin/ai-activity": [],
  "admin/foundation-spine": [],
  "admin/clearance": [],
  "admin/communications": [],
  "admin/directory-link": [],
  "admin/feedback": [],
  "admin/notifications": [],
  "admin/pilot-feedback": [],
  "admin/reports": [],
  "admin/restore-drills": [],
  "admin/roi": [],
  "admin/teams-approvals": [],
  "admin/teams-cards": [],
  "admin/wall-displays": []
};

/** Roots whose pages read different areas, so the map is keyed one level deeper. */
export const TWO_LEVEL_ROOTS: ReadonlySet<string> = new Set(["admin", "header"]);

/** The map key for a query key ("<root>/<second>" for TWO_LEVEL_ROOTS, else the first element). */
export function queryRoot(queryKey: readonly unknown[]): string | null {
  const first = queryKey[0];
  if (typeof first !== "string") return null;
  if (TWO_LEVEL_ROOTS.has(first) && typeof queryKey[1] === "string") return `${first}/${queryKey[1]}`;
  return first;
}

/** The areas a query depends on; ALL when its root is unknown (fail safe). */
export function areasForQuery(queryKey: readonly unknown[]): readonly LiveArea[] | typeof ALL {
  const root = queryRoot(queryKey);
  if (root === null) return ALL;
  return QUERY_ROOT_AREAS[root] ?? QUERY_ROOT_AREAS[String(queryKey[0])] ?? ALL;
}

/** A set of moved areas, or "all" when everything may have moved (API restarted, settings changed). */
export type MovedAreas = ReadonlySet<string> | typeof ALL;

/** True when a query should be refetched because <paramref name="moved"/> moved. */
export function queryMatchesAreas(queryKey: readonly unknown[], moved: MovedAreas): boolean {
  const depends = areasForQuery(queryKey);
  if (moved === ALL) return true;
  if (moved.size === 0) return false;
  if (depends === ALL) return true;
  if (depends.length === 0) return false;
  if (EVERYTHING_AREAS.some((a) => moved.has(a))) return true;
  return depends.some((a) => moved.has(a));
}

/** Merges two moved-area sets ("all" absorbs everything). */
export function mergeMoved(a: MovedAreas | null, b: MovedAreas): MovedAreas {
  if (a === null) return b;
  if (a === ALL || b === ALL) return ALL;
  return new Set([...a, ...b]);
}

/** True when the moved areas reach VIPER's insight cards (whose text the server rewrites about 20 s later). */
export function touchesInsights(moved: MovedAreas): boolean {
  return queryMatchesAreas(["insights"], moved);
}
